import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CmpsDialog, CmpsDialogRef } from '@cmps/ng-uikit';
import { tap } from 'rxjs/operators';

import { AuthService } from '../../auth/auth.service';
import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { StateToProps } from '../../store';
import { useDispatcher, UseStore } from '../../store.decorator';
import {
    ImprovementCategoryDto,
    ImprovementGroupDto,
    PerformanceClassDto,
} from '../core';
import { PopulationGroupDialogComponent } from '../population-group-dialog';
import { useSelector } from '../store-use-selector';
import { TableColumn } from '../table';
import {
    TrendDialogComponent,
    TrendDialogData,
    TrendDialogMode,
} from '../trend-dialog';

import { TranslationKeyMap } from './main.i18n';
import { mainReducer } from './main.reducer';
import { MainState } from './main.state';
import { FilterItemView } from './model/filter-item-view.model';
import { PerformerItemView } from './model/performer-item-view.model';
import { applyFilterSelection } from './state/apply-filter-selection.action';
import { reload } from './state/reload.action';
import {
    hidePopulationGroupDialog,
    showPopulationGroupDialog,
} from './state/show-population-group-dialog.action';
import {
    hideTrendDialog,
    showTrendDialog,
} from './state/show-trend-dialog.action';
import { sortRows } from './state/sort-rows.action';
import { toggleFilterButton } from './state/toggle-filter-button.action';
import { toggleFilterCategory } from './state/toggle-filter-category.action';
import { toggleFilterGroup } from './state/toggle-filter-group.action';

@Component({
    selector: 'cmps-mi2-main',
    templateUrl: './main.component.html',
    host: {
        class: 'cmps-mi2-main',
    },
})
@TakeUntilDestroy()
@UseStore<MainState>('scorecard-mi2.main', mainReducer)
export class MainComponent
    implements OnChanges, TakeUntilDestroy, StateToProps<MainState>
{
    state: MainState;

    /** @private */ _i18n = TranslationKeyMap;
    @Input() userId: number;

    constructor(
        private readonly _authService: AuthService,
        private readonly _dialog: CmpsDialog,
    ) {}

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnChanges(changes: SimpleChanges) {
        if (!this.state) {
            return;
        }

        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('userId')) {
            useDispatcher(this, reload({ userId: this.userId }));
        }
    }

    ngOnInit(): void {
        useDispatcher(this, reload({ userId: this.userId }));
    }

    ngOnDestroy(): void {
        //
    }

    /** @private */
    _onShowPopulationGroupClick(): void {
        if (this.state.populationGroupDialogData != null) {
            return;
        }

        useDispatcher(this, showPopulationGroupDialog({}));

        let ref: CmpsDialogRef<PopulationGroupDialogComponent, any>;
        let didClose = false; // prevent double hideTrendDialog action dispatch
        const sub = useSelector<TrendDialogData>(
            'scorecard-mi2.main.populationGroupDialogData',
            (state) => {
                if (!state) {
                    sub?.unsubscribe();
                    !didClose && ref?.close();
                    return;
                }
                ref = this._dialog.open(PopulationGroupDialogComponent, {
                    movable: true,
                    data: state,
                });
                ref.result
                    .pipe(
                        tap(() => {
                            didClose = true;
                            useDispatcher(this, hidePopulationGroupDialog({}));
                        }),
                        takeUntilDestroy(this),
                    )
                    .subscribe();
            },
        )
            .pipe(takeUntilDestroy(this))
            .subscribe();
    }

    /** @private */
    _onColumnSortClick(
        c: TableColumn,
        sortOrder: 'asc' | 'desc' = 'asc',
    ): void {
        useDispatcher(
            this,
            sortRows({ options: { columnId: c.id, order: sortOrder } }),
        );
    }

    /** @private */
    _onShowTrendClick(r: PerformerItemView, key: TrendDialogMode): void {
        if (this.state.trendDialogData != null) {
            return;
        }

        useDispatcher(
            this,
            showTrendDialog({
                mode: key,
                managerId: this.userId,
                userId: r.userId,
            }),
        );

        let ref: CmpsDialogRef<TrendDialogComponent, any>;
        let didClose = false; // prevent double hideTrendDialog action dispatch
        const sub = useSelector<TrendDialogData>(
            'scorecard-mi2.main.trendDialogData',
            (state) => {
                if (!state) {
                    sub?.unsubscribe();
                    !didClose && ref?.close();
                    return;
                }
                ref = this._dialog.open(TrendDialogComponent, {
                    movable: true,
                    data: state,
                });
                ref.result
                    .pipe(
                        tap(() => {
                            didClose = true;
                            useDispatcher(this, hideTrendDialog({}));
                        }),
                        takeUntilDestroy(this),
                    )
                    .subscribe();
            },
        )
            .pipe(takeUntilDestroy(this))
            .subscribe();
    }

    /** @private */
    _onToggleFilterCategoryClick(cat: ImprovementCategoryDto): void {
        useDispatcher(
            this,
            toggleFilterCategory({
                categoryId: cat.scorecardImprovementCategoryId,
            }),
        );
    }

    /** @private */
    _onToggleFilterGroupClick(grp: ImprovementGroupDto): void {
        useDispatcher(
            this,
            toggleFilterGroup({ groupId: grp.scorecardImprovementGroupId }),
        );
    }

    /** @private */
    _onApplyFilterClick(
        $event: Array<
            FilterItemView<PerformanceClassDto | ImprovementGroupDto>
        >,
    ): void {
        useDispatcher(this, applyFilterSelection({ selection: $event }));
    }

    /** @private */
    _onToggleFilterButtonClick(
        $event: FilterItemView<PerformanceClassDto | ImprovementGroupDto>,
    ): void {
        useDispatcher(this, toggleFilterButton({ item: $event }));
    }
}
